import React, { useCallback, useEffect, useRef, useState } from 'react';
import IFrameResizer from 'iframe-resizer-react';
import PageSpinner from 'components/app/common/pageSpinner';

// Must stay below the API embed token TTL (120s) to leave a clock-skew margin.
const STALE_AFTER_MS = 90 * 1000;

const PENDING_FRAME_STYLE = {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
};

const useMetabaseEmbed = ({ fetchToken, deps = [], refreshMinutes = null }) => {
    const [activeSrc, setActiveSrc] = useState(null);
    const [pendingSrc, setPendingSrc] = useState(null);
    const [isFilterLoading, setIsFilterLoading] = useState(false);

    const fetchTokenRef = useRef(fetchToken);
    fetchTokenRef.current = fetchToken;
    const activeSrcRef = useRef(null);
    const pendingSrcRef = useRef(null);
    const filterLoadingRef = useRef(false);
    const depsKeyRef = useRef(JSON.stringify(deps));
    const depsKey = JSON.stringify(deps);
    if (depsKeyRef.current !== depsKey) {
        const hadFrame = !!activeSrcRef.current || !!pendingSrcRef.current;
        depsKeyRef.current = depsKey;
        if (hadFrame) {
            activeSrcRef.current = null;
            pendingSrcRef.current = null;
            filterLoadingRef.current = true;
            setActiveSrc(null);
            setPendingSrc(null);
            setIsFilterLoading(true);
        }
    }
    const issuedAtRef = useRef(0);
    const inFlightRef = useRef(false);
    const generationRef = useRef(0);
    const mountedRef = useRef(true);

    const isStale = () =>
        !!issuedAtRef.current &&
        Date.now() - issuedAtRef.current > STALE_AFTER_MS;

    const mint = useCallback(async (supersede = false, reason = 'tick') => {
        if (inFlightRef.current && !supersede) return;
        generationRef.current += 1;
        const generation = generationRef.current;
        inFlightRef.current = true;
        try {
            const src = await fetchTokenRef.current();
            if (
                !src ||
                !mountedRef.current ||
                generation !== generationRef.current
            ) {
                if (
                    filterLoadingRef.current &&
                    generation === generationRef.current
                ) {
                    filterLoadingRef.current = false;
                    setIsFilterLoading(false);
                }
                return;
            }
            issuedAtRef.current = Date.now();
            if (activeSrcRef.current) {
                pendingSrcRef.current = src;
                setPendingSrc(src);
            } else {
                activeSrcRef.current = src;
                setActiveSrc(src);
            }
        } catch (error) {
            console.error(error);
        } finally {
            if (generation === generationRef.current) {
                inFlightRef.current = false;
            }
        }
    }, []);

    const refresh = useCallback(() => mint(), [mint]);

    const onLoad = useCallback(
        (src) => {
            if (src && src === pendingSrcRef.current) {
                activeSrcRef.current = src;
                pendingSrcRef.current = null;
                setActiveSrc(src);
                setPendingSrc(null);
                return;
            }
            if (src === activeSrcRef.current && filterLoadingRef.current) {
                filterLoadingRef.current = false;
                setIsFilterLoading(false);
            }
            if (src === activeSrcRef.current && isStale()) {
                mint(false, 'stale-onload');
            }
        },
        [mint]
    );

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        mint(true, 'deps');
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const minutes = Number(refreshMinutes);
        if (!minutes || minutes <= 0) return undefined;
        const intervalId = setInterval(
            () => mint(false, 'interval'),
            minutes * 60 * 1000
        );
        return () => clearInterval(intervalId);
    }, [refreshMinutes, mint]);

    useEffect(() => {
        const handleReturn = () => {
            if (document.visibilityState !== 'visible') return;
            if (isStale()) mint(false, 'tab-return');
        };
        document.addEventListener('visibilitychange', handleReturn);
        window.addEventListener('focus', handleReturn);
        return () => {
            document.removeEventListener('visibilitychange', handleReturn);
            window.removeEventListener('focus', handleReturn);
        };
    }, [mint]);

    return {
        activeSrc,
        pendingSrc,
        onLoad,
        refresh,
        isInitialLoading: !activeSrc,
        isFilterLoading,
    };
};

export const MetabaseEmbedFrame = ({
    embed,
    onLoad,
    style,
    className,
    wrapperClassName,
}) => {
    const { activeSrc, pendingSrc } = embed;
    const sources = [activeSrc, pendingSrc].filter(
        (src, index, list) => src && list.indexOf(src) === index
    );
    const visibleSources = embed.isFilterLoading
        ? sources.filter((src) => src === activeSrc)
        : sources;

    return (
        <div
            className={wrapperClassName}
            style={{ position: 'relative', width: '100%' }}
        >
            {embed.isFilterLoading && (
                <PageSpinner
                    type="Oval"
                    color="#ACBDC9"
                    height={45}
                    width={45}
                    msg="Fetching data..."
                />
            )}
            {visibleSources.map((src) => (
                <IFrameResizer
                    key={src}
                    src={src}
                    className={className}
                    style={
                        embed.isFilterLoading || src !== activeSrc
                            ? { ...style, ...PENDING_FRAME_STYLE }
                            : style
                    }
                    onLoad={(event) => {
                        embed.onLoad(src);
                        if (onLoad) onLoad(event);
                    }}
                />
            ))}
        </div>
    );
};

export default useMetabaseEmbed;
